import { useEffect, useState } from "react";
import { breakpoints } from "../styles/tokens";

/**
 * Three-state viewport tier — the structural responsive signal.
 *
 *   phone   < md  (768)       — true handheld; the bottom-sheet / single-column shell
 *   tablet  md … xl-1 (768–1365) — capped column (+ optional rail), centered modals
 *   desktop ≥ xl  (1366)       — full sidebar + right-rail + footer shell
 *
 * Cutoffs mirror the design tokens (`$breakpoint-md` / `$breakpoint-xl`) and
 * the SCSS `tablet-only` mixin — never hand-tune these. `useIsMobileOrTablet`
 * stays the boolean `tier !== 'desktop'`; this hook is for code that must tell
 * tablet apart from phone.
 */
export type DeviceTier = "phone" | "tablet" | "desktop";

// Mirror `breakpoint-down`: each band runs up to `breakpoint - 1px`.
const PHONE_MAX = breakpoints.md - 1; // 767 — below this is a true phone
const TABLET_MAX = breakpoints.xl - 1; // 1365 — at/above xl is desktop

function readTier(): DeviceTier {
  if (typeof window === "undefined") return "desktop";
  const width = window.innerWidth;
  if (width <= PHONE_MAX) return "phone";
  if (width <= TABLET_MAX) return "tablet";
  return "desktop";
}

export function useDeviceTier(): DeviceTier {
  const [tier, setTier] = useState<DeviceTier>(readTier);

  useEffect(() => {
    const phoneMql = window.matchMedia(`(max-width: ${PHONE_MAX}px)`);
    const tabletMql = window.matchMedia(`(max-width: ${TABLET_MAX}px)`);
    const update = () =>
      setTier(phoneMql.matches ? "phone" : tabletMql.matches ? "tablet" : "desktop");

    update();
    phoneMql.addEventListener("change", update);
    tabletMql.addEventListener("change", update);
    return () => {
      phoneMql.removeEventListener("change", update);
      tabletMql.removeEventListener("change", update);
    };
  }, []);

  return tier;
}
